import {
  Track,
  type AudioProcessorOptions,
  type TrackProcessor,
} from 'livekit-client';

const clampGain = (value: number) => Math.min(4, Math.max(0.25, value));

export default class MicrophoneGainProcessor
  implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions>
{
  readonly name = 'geoescala-microphone-gain';
  processedTrack?: MediaStreamTrack;

  private gainValue: number;
  private source?: MediaStreamAudioSourceNode;
  private gainNode?: GainNode;
  private destination?: MediaStreamAudioDestinationNode;
  private audioContext?: AudioContext;

  constructor(initialGain = 1) {
    this.gainValue = clampGain(initialGain);
  }

  setGain(value: number) {
    this.gainValue = clampGain(value);
    if (this.gainNode) {
      const currentTime = this.gainNode.context.currentTime;
      this.gainNode.gain.cancelScheduledValues(currentTime);
      this.gainNode.gain.setValueAtTime(this.gainValue, currentTime);
    }
  }

  async init({ track, audioContext }: AudioProcessorOptions) {
    this.audioContext = audioContext;
    const sourceStream = new MediaStream([track]);
    this.source = audioContext.createMediaStreamSource(sourceStream);
    this.gainNode = audioContext.createGain();
    this.destination = audioContext.createMediaStreamDestination();
    this.gainNode.gain.setValueAtTime(
      this.gainValue,
      audioContext.currentTime,
    );
    this.source.connect(this.gainNode);
    this.gainNode.connect(this.destination);
    this.processedTrack = this.destination.stream.getAudioTracks()[0];
  }

  async restart(options: AudioProcessorOptions) {
    const audioContext = options.audioContext || this.audioContext;
    if (!audioContext) return;
    await this.destroy();
    await this.init({ ...options, audioContext });
  }

  async destroy() {
    this.source?.disconnect();
    this.gainNode?.disconnect();
    this.processedTrack?.stop();
    this.source = undefined;
    this.gainNode = undefined;
    this.destination = undefined;
    this.processedTrack = undefined;
  }
}
